import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase'; // Import the db instance we created above
import { ReportStatus, BorrowerResponseType } from '../components/constants';

const REPORTS_COLLECTION = 'payback-reports';
const USERS_COLLECTION = 'payback-users';
const SESSION_KEY = 'payback_session_user'; // Local cache for current user

export const MAX_DAILY_POSTS = 3;
export const KYC_THRESHOLD = 10;

/**
 * INIT DATA
 * Firestore doesn't need explicit init, but you can run this once to seed data if needed.
 */
export const initData = async () => {
  // Optional: Check if collection is empty and seed
  // Usually skipped in production Firestore apps
};

/**
 * GET REPORTS (Async)
 * Fetches all reports.
 */
export const getReports = async () => {
  try {
    const q = query(collection(db, REPORTS_COLLECTION), orderBy('createdDate', 'desc'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error("Error getting reports:", error);
    return [];
  }
};

/**
 * GET PUBLIC REPORTS (Async)
 * Fetches reports and filters them logic.
 * Note: Firestore filtering is limited, so we fetch active reports and refine in JS.
 */
export const getPublicReports = async (searchTerm) => {
  try {
    // 1. Fetch all non-withdrawn reports
    // We can't easily filter by "contains string" in Firestore without external tools (Algolia/Typesense)
    // So we fetch the collection and filter in memory for this scale.
    const q = query(
      collection(db, REPORTS_COLLECTION), 
      where('status', '!=', ReportStatus.WITHDRAWN)
    );
    
    const querySnapshot = await getDocs(q);
    const all = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    const now = new Date();
    const GRACE_PERIOD_DAYS = 7;

    return all.filter(r => {
      // Search Filter (Client-side)
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesMain = 
          (r.borrowerName || '').toLowerCase().includes(term) || 
          (r.borrowerMobile || '').includes(searchTerm) ||
          r.id.includes(searchTerm);
        
        const matchesCoBorrower = r.additionalBorrowers?.some(b => 
            (b.name || '').toLowerCase().includes(term) || (b.mobile || '').includes(searchTerm)
        );

        if (!matchesMain && !matchesCoBorrower) return false;
      }

      // Status / Grace Period Logic
      if (
        r.status === ReportStatus.PUBLISHED_SETTLED || 
        r.status === ReportStatus.PUBLISHED_DISPUTED || 
        r.status === ReportStatus.PUBLISHED_UNRESOLVED
      ) {
        return true;
      }

      if (r.status === ReportStatus.PENDING_RESPONSE) {
        const created = new Date(r.createdDate);
        const diffTime = Math.abs(now.getTime() - created.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > GRACE_PERIOD_DAYS; 
      }

      return true;
    });
  } catch (error) {
    console.error("Error getting public reports:", error);
    return [];
  }
};

/**
 * SAVE/UPDATE REPORT (Async)
 */
export const saveReport = async (report) => {
  try {
    // If report has an ID and it looks like a Firestore ID (usually 20 chars), we update/set
    // If it was a random math random ID from the old code, we might want to let Firestore generate one,
    // but for migration, we will use the ID provided in the object.
    
    const reportRef = doc(db, REPORTS_COLLECTION, report.id);
    
    // Ensure data is plain objects
    const reportData = JSON.parse(JSON.stringify(report)); 
    
    await setDoc(reportRef, reportData, { merge: true });
    return reportData;
  } catch (error) {
    console.error("Error saving report:", error);
    throw error;
  }
};

/**
 * GET USER (Sync - Session Cache)
 * We keep this synchronous to avoid breaking all your components that call `getUser()`.
 * But `loginUser` handles the async sync to Firestore.
 */
export const getUser = () => {
  const user = localStorage.getItem(SESSION_KEY);
  return user ? JSON.parse(user) : null;
};

/**
 * GET USER FROM DB (Async)
 * Useful for refreshing profile data
 */
export const fetchUserFromDb = async (userId) => {
  try {
    const docRef = doc(db, USERS_COLLECTION, userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error("Error fetching user:", error);
    return null;
  }
};

/**
 * LOGIN / REGISTER USER (Async)
 * Creates/Updates user in Firestore AND sets LocalStorage session.
 */
export const loginUser = async (name, email, mobile, roles) => {
  try {
    // In a real app, you'd use Firebase Auth UID here.
    // For this migration, we generate an ID or use email/mobile as key if we wanted unique.
    // Let's generate a consistent ID based on mobile to simulate "logging back in"
    const safeId = mobile.replace(/[^a-zA-Z0-9]/g, ''); 
    const userId = `user-${safeId}`;

    const userRef = doc(db, USERS_COLLECTION, userId);
    const userSnap = await getDoc(userRef);

    let userData;

    if (userSnap.exists()) {
      // User exists, update roles if changed, keep verification status
      const current = userSnap.data();
      userData = {
        ...current,
        name, // Update name if changed
        email, // Update email if changed
        roles // Update roles
      };
    } else {
      // New User
      userData = { 
        id: userId,
        name, 
        email, 
        mobile, 
        roles,
        isVerified: false,
        createdAt: new Date().toISOString()
      };
    }

    await setDoc(userRef, userData, { merge: true });
    
    // Set Session
    localStorage.setItem(SESSION_KEY, JSON.stringify(userData));
    return userData;
  } catch (error) {
    console.error("Error logging in:", error);
    throw error;
  }
};

/**
 * UPDATE USER (Async)
 */
export const updateUser = async (user) => {
  try {
    const userRef = doc(db, USERS_COLLECTION, user.id);
    await setDoc(userRef, user, { merge: true });
    
    // Update Session
    localStorage.setItem(SESSION_KEY, JSON.stringify(user));
  } catch (error) {
    console.error("Error updating user:", error);
  }
};

export const logoutUser = () => {
  localStorage.removeItem(SESSION_KEY);
};

/**
 * STATS (Async)
 */
export const getDailyPostStats = async (userId) => {
  try {
    const today = new Date().toISOString().split('T')[0];
    
    // Firestore stores dates as Strings (ISO) or Timestamps. 
    // Assuming ISO string for simplicity based on your previous code.
    // A simplified query:
    const q = query(
      collection(db, REPORTS_COLLECTION),
      where('lenderId', '==', userId),
      where('createdDate', '>=', today) 
    );
    
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error("Error getting stats:", error);
    return 0;
  }
};

export const getTotalReportsCount = async (userId) => {
  try {
    const q = query(
      collection(db, REPORTS_COLLECTION),
      where('lenderId', '==', userId)
    );
    // Note: for large collections, use count() aggregation, 
    // but snapshot.size is fine for this scale.
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    return 0;
  }
};

export const verifyUser = async (userId) => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    const updates = { 
      isVerified: true, 
      verificationDate: new Date().toISOString() 
    };
    await updateDoc(userRef, updates);

    // Update session if it's the current user
    const currentUser = getUser();
    if (currentUser && currentUser.id === userId) {
      localStorage.setItem(SESSION_KEY, JSON.stringify({ ...currentUser, ...updates }));
    }
  } catch (error) {
    console.error("Error verifying user:", error);
  }
};